import React, { useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { AttendeeState, MeetingAttendee } from "../../meetings/meetingTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { GuestsList } from "./GuestsList"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { faHandshake } from "@fortawesome/pro-light-svg-icons"
import { isEmpty } from "lodash"
import { ActionBarItemPrintGuestLabels } from "./ActionBarItemPrintGuestLabels"
import { PrintLabelsModal } from "../checkIn/PrintLabelsModal"
import { GuestData } from "../../../external/brotherPrinter"
import { GuestCheckInModal } from "../checkIn/GuestCheckInModal"
import { ActionBarDrawer } from "./ActionBarDrawer"

const useStyles = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: ".5rem",
    },
}))

export type ManageAllButtonMenuOption = {
    label: string
    onClick: () => void
}

type GuestsSectionProps = {
    guests: MeetingAttendee[] | undefined
}

type GuestListSections = "checkedIn" | "notCheckedIn"

export function GuestsSection({ guests }: GuestsSectionProps) {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const [selectedNotCheckedIds, setSelectedNotCheckedIds] = useState<number[]>([])
    const [selectedCheckedIds, setSelectedCheckedIds] = useState<number[]>([])
    const [printLabelModal, setPrintLabelModal] = useState(false)
    const [printLabelData, setPrintLabelData] = useState<Array<GuestData>>()
    const [checkInModal, setCheckInModal] = useState(false)
    const [checkInData, setCheckInData] = useState<Array<MeetingAttendee>>()
    const [expanded, setExpanded] = React.useState<GuestListSections | false>("notCheckedIn")

    const handleChange = (panel: GuestListSections) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false)
    }

    function toggleSelection(setterFunction: React.Dispatch<React.SetStateAction<number[]>>, id: number): void {
        setterFunction((prevState) => {
            if (prevState.includes(id)) {
                return prevState.filter((selectedId) => selectedId !== id)
            } else {
                return [...prevState, id]
            }
        })
    }

    const handleSelectNotChecked = (id: number) => {
        toggleSelection(setSelectedNotCheckedIds, id)
    }

    const handleSelectChecked = (id: number) => {
        toggleSelection(setSelectedCheckedIds, id)
    }

    const handleClearSelected = () => {
        setSelectedNotCheckedIds([])
        setSelectedCheckedIds([])
    }

    const checkedInGuests = guests?.filter((person) => person.state === AttendeeState.CHECKED_IN)
    const notCheckedInGuests = guests?.filter((person) => person.state === AttendeeState.EXPECTED)

    const notCheckedInMenuOptions: ManageAllButtonMenuOption[] = [
        {
            label: l10n.getString("service-center-reports-dashboard-meetings-drawer-manage-all-bulk-check-in"),
            onClick: () => {
                setCheckInModal(true)
                setCheckInData(notCheckedInGuests)
            },
        },
        {
            label: l10n.getString("service-center-reports-dashboard-meetings-drawer-manage-all-bulk-print-labels"),
            onClick: () => {
                setPrintLabelModal(true)
                setPrintLabelData(
                    notCheckedInGuests?.map((guest) => ({
                        visitorName: guest.name,
                        hostName: guest.companyName,
                        visitorCompany: guest.companyName,
                    })) || [],
                )
            },
        },
    ]

    const checkedInMenuOptions: ManageAllButtonMenuOption[] = [
        {
            label: l10n.getString("service-center-reports-dashboard-meetings-drawer-manage-all-bulk-print-labels"),
            onClick: () => {
                setPrintLabelModal(true)
                setPrintLabelData(
                    checkedInGuests?.map((guest) => ({
                        visitorName: guest.name,
                        hostName: guest.companyName,
                        visitorCompany: guest.companyName,
                    })) || [],
                )
            },
        },
    ]

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-guests-title">
                    <Typography variant="h5" fontWeight={500}>
                        Guests
                    </Typography>
                </LocalizedStrict>
            </Box>
            <Stack direction="column" spacing="2rem">
                <LocalizedStrict
                    id="service-center-reports-dashboard-meetings-drawer-guests-list-not-checked-title"
                    attrs={{ title: true }}
                >
                    <GuestsList
                        title="Not checked in"
                        guests={notCheckedInGuests}
                        selectedIds={selectedNotCheckedIds}
                        onSelectGuest={handleSelectNotChecked}
                        handleChange={handleChange("notCheckedIn")}
                        expanded={expanded === "notCheckedIn"}
                        menuOptions={notCheckedInMenuOptions}
                    />
                </LocalizedStrict>
                <LocalizedStrict
                    id="service-center-reports-dashboard-meetings-drawer-guests-list-checked-title"
                    attrs={{ title: true }}
                >
                    <GuestsList
                        title="Checked in"
                        guests={checkedInGuests}
                        selectedIds={selectedCheckedIds}
                        onSelectGuest={handleSelectChecked}
                        handleChange={handleChange("checkedIn")}
                        expanded={expanded === "checkedIn"}
                        menuOptions={checkedInMenuOptions}
                    />
                </LocalizedStrict>

                {(!isEmpty(selectedNotCheckedIds) || !isEmpty(selectedCheckedIds)) && (
                    <ActionBarDrawer
                        selectedItemsLength={selectedNotCheckedIds.length + selectedCheckedIds.length}
                        handleCloseActionBar={handleClearSelected}
                    >
                        {isEmpty(selectedCheckedIds) && (
                            <LocalizedStrict
                                id="service-center-reports-dashboard-meetings-drawer-guests-list-floating-check-action"
                                attrs={{ title: true }}
                            >
                                <ActionBarItem
                                    icon={faHandshake}
                                    title="Check in"
                                    onClick={() => {
                                        setCheckInModal(true)
                                        setCheckInData(
                                            notCheckedInGuests?.filter((guest) =>
                                                selectedNotCheckedIds.includes(guest.id),
                                            ),
                                        )
                                    }}
                                />
                            </LocalizedStrict>
                        )}
                        <ActionBarItemPrintGuestLabels
                            onClick={() => {
                                setPrintLabelModal(true)
                                setPrintLabelData(
                                    guests
                                        ?.filter(
                                            (guest) =>
                                                selectedCheckedIds.includes(guest.id) ||
                                                selectedNotCheckedIds.includes(guest.id),
                                        )
                                        .map((guest) => ({
                                            visitorName: guest.name,
                                            hostName: guest.companyName,
                                            visitorCompany: guest.companyName,
                                        })) || [],
                                )
                            }}
                        />
                    </ActionBarDrawer>
                )}
            </Stack>
            {printLabelModal && printLabelData?.length && (
                <PrintLabelsModal
                    labels={printLabelData}
                    open={printLabelModal}
                    onClose={() => {
                        setPrintLabelModal(false)
                    }}
                />
            )}

            {checkInModal && checkInData?.length && (
                <GuestCheckInModal
                    guests={checkInData}
                    open={checkInModal}
                    onClose={() => {
                        setCheckInModal(false)
                    }}
                />
            )}
        </Box>
    )
}
